import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertIntermediateElementBody,
  IntermediateElement,
  UpdateIntermediateElementBody,
} from "./types/intermediateElements";

export const useIntermediateElementsApi = () => {
  const api = useApi();

  const getAdminIntermediateElement = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/vertigrip/intermediate_elements`;
      return useSWRMutation<IntermediateElement, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<IntermediateElement>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useInstallationTypeAdminIntermediateElements = useCallback(
    (installationTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/intermediate_elements`,
        query,
      );
      return useSWR<Paginated<IntermediateElement>, CustomError, string>(url, (url) =>
        api.get<Paginated<IntermediateElement>>({ url, signal }),
      );
    },
    [api],
  );

  const useSystemTypeAdminIntermediateElements = useCallback(
    (systemTypeId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/system_types/${systemTypeId}/intermediate_elements`, query);
      return useSWR<Paginated<IntermediateElement>, CustomError, string>(url, (url) =>
        api.get<Paginated<IntermediateElement>>({ url, signal }),
      );
    },
    [api],
  );

  const useSteelVersionAdminIntermediateElements = useCallback(
    (steelVersionId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/vertigrip/steel_versions/${steelVersionId}/intermediate_elements`, query);
      return useSWR<Paginated<IntermediateElement>, CustomError, string>(url, (url) =>
        api.get<Paginated<IntermediateElement>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminIntermediateElement = useCallback(
    async (body: InsertIntermediateElementBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/intermediate_elements`;
      const id = await api.post<{ id: string }, InsertIntermediateElementBody>(url, body);
      mutateCache([
        url,
        `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/system_types/${body.systemTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/intermediate_elements`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminIntermediateElement = useCallback(
    async (intermediateElementId: string, body: UpdateIntermediateElementBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/intermediate_elements/${intermediateElementId}`;
      (await api.put<UpdateIntermediateElementBody>(url, body),
        mutateCache([
          `/admin/settings/vertigrip/intermediate_elements`,
          `/admin/settings/vertigrip/installation_types/${body.installationTypeId}/intermediate_elements`,
          `/admin/settings/vertigrip/system_types/${body.systemTypeId}/intermediate_elements`,
          `/admin/settings/vertigrip/steel_versions/${body.steelVersionId}/intermediate_elements`,
        ]));
    },
    [api],
  );

  const deleteAdminIntermediateElement = useCallback(
    async (
      intermediateElementId: string,
      steelVersionId: string,
      installationTypeId: string | null | undefined,
      systemTypeId: string | null | undefined,
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/intermediate_elements/${intermediateElementId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/vertigrip/intermediate_elements`,
        `/admin/settings/vertigrip/installation_types/${installationTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/system_types/${systemTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/steel_versions/${steelVersionId}/intermediate_elements`,
      ]);
    },
    [api],
  );

  const updateAdminIntermediateElementVisibility = useCallback(
    async (
      intermediateElementId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { installationTypeId?: string; steelVersionId?: string; systemTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/intermediate_elements/${intermediateElementId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/vertigrip/intermediate_elements",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/system_types/${options?.systemTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/steel_versions/${options?.steelVersionId}/intermediate_elements`,
      ]);
    },
    [api],
  );

  const updateAdminIntermediateElementOrder = useCallback(
    async (
      intermediateElementId: string,
      order: UpdateOrderRequestBody,
      options?: { installationTypeId?: string; steelVersionId?: string; systemTypeId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/intermediate_elements/${intermediateElementId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        "/admin/settings/vertigrip/intermediate_elements",
        `/admin/settings/vertigrip/installation_types/${options?.installationTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/system_types/${options?.systemTypeId}/intermediate_elements`,
        `/admin/settings/vertigrip/steel_versions/${options?.steelVersionId}/intermediate_elements`,
      ]);
    },
    [api],
  );

  return {
    getAdminIntermediateElement,
    useInstallationTypeAdminIntermediateElements,
    useSystemTypeAdminIntermediateElements,
    useSteelVersionAdminIntermediateElements,
    insertAdminIntermediateElement,
    updateAdminIntermediateElement,
    updateAdminIntermediateElementVisibility,
    updateAdminIntermediateElementOrder,
    deleteAdminIntermediateElement,
  };
};
